import { useState, useEffect } from 'react';
import './ChapterNav.css';

const ChapterNav = ({ chapters }) => {
    // chapters: [{ id: 'section-id', label: 'Display Name' }, ...]
    const [activeId, setActiveId] = useState(null);
    const [visible, setVisible] = useState(false);

    // IntersectionObserver to track active chapter
    useEffect(() => {
        const elements = chapters
            .map(ch => document.getElementById(ch.id))
            .filter(Boolean);

        if (elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Find the entry most in view
                const intersecting = entries.filter(e => e.isIntersecting);
                if (intersecting.length > 0) {
                    // Pick the one with highest intersection ratio
                    const best = intersecting.reduce((a, b) =>
                        a.intersectionRatio > b.intersectionRatio ? a : b
                    );
                    setActiveId(best.target.id);
                }
            },
            { rootMargin: '-10% 0px -40% 0px', threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
        );

        elements.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [chapters]);

    // Show nav after scrolling past ~300px
    useEffect(() => {
        const handleScroll = () => {
            setVisible(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id) => {
        setActiveId(id);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <nav
            className={`ChapterNav ${visible ? 'visible' : ''}`}
            aria-label="Chapter navigation"
        >
            <div className="ChapterNav-list">
                {chapters.map((ch) => (
                    <button
                        key={ch.id}
                        className={`ChapterNav-item ${activeId === ch.id ? 'active' : ''}`}
                        onClick={() => scrollTo(ch.id)}
                        aria-current={activeId === ch.id ? 'true' : undefined}
                    >
                        <span className="ChapterNav-arrow" aria-hidden="true">&rsaquo;</span>
                        <span className="ChapterNav-label">{ch.label}</span>
                    </button>
                ))}
            </div>
        </nav>
    );
};

export default ChapterNav;
